import React, {useEffect, useState, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  List,
  ListItem,
  Typography,
  Box,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
} from '@mui/material';
import SearchGreyIcon from '../assets/search-grey.svg';
import {clearSharedDocumentData} from '../util/sharedDocumentStorage';
import TimeAgo from './TimeAgo';
import {useGetOrgsQuery} from '../data/api';
import {useDispatch, useSelector} from 'react-redux';
import {setInscopeOrgs, setOrg, setOrgConfig} from '../data/session';
import Loading from './Loading';
import {PSO_ICON_COLORS, ROLE_COLORS} from '../constants';
import {APP_HEADER_HEIGHT} from '../theme';
import ArrowRightIcon from '../assets/arrow-right.svg';
import {useFlags} from 'launchdarkly-react-client-sdk';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';

const getColorByOrgHash = (orgId) => {
  if (!orgId) return PSO_ICON_COLORS[0];

  let hash = 0;
  for (let i = 0; i < orgId.length; i++) {
    const char = orgId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const index = Math.abs(hash) % PSO_ICON_COLORS.length;
  return PSO_ICON_COLORS[index];
};

const styles = {
  header: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginLeft: '3rem',
    marginRight: '3rem',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    paddingTop: `calc(${APP_HEADER_HEIGHT} + 2.5rem)`,
    paddingBottom: '1.25rem',
    width: '100%',
    maxWidth: '1268px',
  },
  scrollableContent: {
    flex: 1,
    width: '100%',
    maxWidth: '1268px',
    overflowY: 'auto',
    paddingBottom: '2rem',
  },
  greeting: {
    marginBottom: '2.25rem !important',
    color: '#162523',
    fontSize: '32px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.32px',
  },
  title: {
    marginBottom: '0.5rem',
  },
  listItem: {
    maxWidth: '1268px',
    border: '1px solid #DFE5E2',
    marginBottom: '0.875rem',
    borderRadius: '12px',
    cursor: 'pointer',
    padding: '1.5rem 2.5rem',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      border: '1px solid #054A41',
    },
  },
  subTitle: {
    color: '#657O6D',
    fontWeight: 400,
  },
  searchField: {
    display: 'flex',
    width: '320px',
    padding: '8px 12px',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '8px',
    border: '1px solid #E1E5E4',
    backgroundColor: '#FFFFFF',
  },
  inputField: {
    border: 'none',
    outline: 'none',
    width: '100%',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '140%',
    letterSpacing: '-0.14px',
  },
  dropdown: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    width: '201px',
    height: '40px',
    padding: '8px 4px 8px 12px',
    borderRadius: '8px',
    border: '1px solid #E1E5E4',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-select': {
      padding: '0',
    },
    '& .MuiSelect-icon': {
      color: '#65706D',
    },
  },
  dropdownMenu: {
    '& .MuiPaper-root': {
      borderRadius: '4px',
      border: '1px solid #E1E5E4',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    },
    '& .MuiMenuItem-root': {
      color: '#162523',
      fontSize: '14px',
      padding: '8px 12px',
      '&:hover': {
        backgroundColor: '#F5F7F6',
      },
      '&.Mui-selected': {
        backgroundColor: '#F5F7F6 !important',
        color: '#162523 !important',
        '&:hover': {
          backgroundColor: '#F5F7F6 !important',
        },
      },
      '&.Mui-focusVisible': {
        backgroundColor: '#F5F7F6 !important',
      },
    },
  },
  cardStyle: {
    display: 'flex',
    padding: '24px',
    alignItems: 'center',
    gap: '40px',
    alignSelf: 'stretch',
    borderRadius: '12px',
    border: '1px solid #E1E5E4',
  },
  companyName: {
    color: '#162523',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: '-0.18px',
  },
  subtleText: {
    color: '#819190',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '140%',
    letterSpacing: '-0.12px',
  },
  subtleSubtitle: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  roleSubtitle: {
    display: 'flex',
    height: '24px',
    width: 'fit-content',
    padding: '0 12px',
    alignItems: 'center',
    gap: '12px',
    color: '#162523',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.13px',
    borderRadius: '100px',
  },
  initialIcon: {
    width: '46px',
    height: '46px',
    borderRadius: '50%',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '140%',
    marginRight: '1rem',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    textAlign: 'center',
  },
  emptyStateTitle: {
    color: '#162523',
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '0.5rem',
    lineHeight: '150%',
  },
  emptyStateSubtitle: {
    color: '#65706D',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '140%',
  },
  exportButton: {
    color: '#162523',
    fontSize: '14px',
    fontWeight: 500,
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #E1E5E4',
    textTransform: 'none',
    backgroundColor: '#FFFFFF',
    minHeight: '40px !important',
    height: '40px !important',
    '&:hover': {
      backgroundColor: '#F5F7F6',
      border: '1px solid #054A41',
    },
  },
};

const filterOptions = [
  {label: 'Most recent activity', value: 'mostRecentActivity'},
  {label: 'A to Z', value: 'abc'},
  {label: 'Z to A', value: 'cba'},
];

const ParentOrgPage = ({userFirstName}) => {
  const dispatch = useDispatch();
  const inscopeOrgs = useSelector((state) => state.session.inscopeOrgs);
  const {currentOrg} = useSelector((state) => state.session);
  const {
    documentFolders: documentFoldersEnabled,
    headOfficeOnParentOrganizationPage:
      headOfficeOnParentOrganizationPageEnabled,
  } = useFlags();
  const inscopeParentOrg = useMemo(
    () => inscopeOrgs.find(({isParentOrganization}) => isParentOrganization),
    [inscopeOrgs]
  );

  useEffect(() => {
    if (inscopeParentOrg && (!currentOrg || !currentOrg.orgId)) {
      dispatch(setOrg({org: inscopeParentOrg}));
    }
  }, [inscopeParentOrg, currentOrg, dispatch]);

  const {
    data: orgs,
    isFetching,
    isSuccess,
    isError,
  } = useGetOrgsQuery({
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const nonParentOrgs = useMemo(
    () =>
      inscopeOrgs
        .filter(({isParentOrganization}) => !isParentOrganization)
        .sort(
          (a, b) =>
            new Date(b.mostRecentActivity || 0) -
            new Date(a.mostRecentActivity || 0)
        ),
    [inscopeOrgs]
  );

  const headOfficeOptions = useMemo(() => {
    return nonParentOrgs
      ?.reduce((acc, org) => {
        if (org.headOfficeId && !acc.includes(org.headOfficeId)) {
          acc.push(org.headOfficeId);
        }
        return acc;
      }, [])
      .sort();
  }, [nonParentOrgs]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDropdown, setSelectedDropdown] = useState(
    filterOptions[0].value
  );
  const [selectedHeadOffices, setSelectedHeadOffices] = useState([]);
  const headOfficeSet = useMemo(
    () => new Set(selectedHeadOffices),
    [selectedHeadOffices]
  );
  const filteredOrgs = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();

    const result = nonParentOrgs.filter((org) => {
      const matchesSearch =
        searchQuery.length === 0 ||
        (org.name || '').toLowerCase().includes(searchLower);
      const matchesHeadOffice =
        selectedHeadOffices.length === 0 || headOfficeSet.has(org.headOfficeId);
      return matchesSearch && matchesHeadOffice;
    });

    if (selectedDropdown === 'mostRecentActivity') {
      result.sort(
        (a, b) =>
          new Date(b.mostRecentActivity || 0) -
          new Date(a.mostRecentActivity || 0)
      );
    } else if (selectedDropdown === 'abc') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (selectedDropdown === 'cba') {
      result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    }

    if (selectedHeadOffices.length > 0) {
      result.sort((a, b) =>
        (a.headOfficeId || '').localeCompare(b.headOfficeId || '')
      );
    }

    return result;
  }, [nonParentOrgs, searchQuery, selectedHeadOffices, selectedDropdown]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDropdownChange = (e) => {
    setSelectedDropdown(e.target.value);
  };

  const handleHeadOfficeChange = (newValue) => {
    setSelectedHeadOffices(newValue);
  };

  const formatFiscalYearEnd = useCallback((fiscalYearEnd) => {
    const mmddPattern = /^\d{2}-\d{2}$/;
    if (!fiscalYearEnd || !mmddPattern.test(fiscalYearEnd)) return 'Unset';

    const [month, day] = fiscalYearEnd.split('-');
    const date = new Date(2000, parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
  }, []);

  const handleOrgClick = useCallback((org) => {
    localStorage.setItem('selectedOrgId', org);
    clearSharedDocumentData();
    window.location.href = documentFoldersEnabled ? '/folders' : '/documents';
  }, []);

  const handleExportToExcel = useCallback(() => {
    const hasExternalIds = filteredOrgs.some((org) => org.externalId);
    const hasHeadOfficeIds = filteredOrgs.some((org) => org.headOfficeId);

    const exportData = filteredOrgs
      .map((org) => {
        let clientName = org.name || 'Unset';

        // If externalId exists and is in the name, strip it out
        if (org.externalId && clientName.includes(org.externalId)) {
          clientName = clientName.replace(org.externalId, '').trim();
          clientName = clientName.replace(/^[-\s]+|[-\s]+$/g, '').trim();
        }

        const row = {
          'Client Name': clientName,
        };

        if (hasExternalIds) {
          row['Client Number'] = org.externalId || '';
        }

        if (hasHeadOfficeIds) {
          row['Head Office'] = org?.headOfficeId || '';
        }

        row.Role = org.userAssignedRole || 'Unset';
        row['Last Activity'] = org.mostRecentActivity
          ? new Date(org.mostRecentActivity).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : 'No activity';

        return row;
      })
      .sort((a, b) =>
        a['Client Name'].localeCompare(b['Client Name'], 'en', {
          sensitivity: 'base',
        })
      );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(data, `clients-export-${timestamp}.xlsx`);
  }, [filteredOrgs, formatFiscalYearEnd]);

  useEffect(() => {
    if (inscopeParentOrg) {
      dispatch(setOrgConfig({orgConfig: inscopeParentOrg}));
    }
  }, [inscopeParentOrg, dispatch]);

  useEffect(() => {
    if (isSuccess && !isFetching && !isError && orgs) {
      dispatch(setInscopeOrgs({inscopeOrgs: orgs}));
    }
  }, [orgs, isSuccess, isFetching, isError, dispatch]);

  return (
    <div style={styles.header}>
      <div style={styles.stickyHeader}>
        <Stack spacing={1} sx={{width: '100%'}}>
          <Typography variant={'h4'} sx={styles.greeting}>
            {`Hi ${userFirstName}, welcome back`}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            sx={{width: '100%'}}
          >
            <Box sx={styles.searchField}>
              <img src={SearchGreyIcon} height={20} width={20} alt={'search'} />
              <Box
                component="input"
                type="text"
                placeholder="Search clients"
                sx={{
                  ...styles.inputField,
                  '&::placeholder': {
                    color: '#9AA7A6',
                  },
                }}
                onChange={(e) => handleSearch(e)}
              />
            </Box>
            <Stack direction="row" spacing={1}>
              {headOfficeOnParentOrganizationPageEnabled && (
                <Autocomplete
                  options={headOfficeOptions}
                  multiple
                  size="small"
                  disableCloseOnSelect
                  value={selectedHeadOffices}
                  onChange={(event, newValue) =>
                    handleHeadOfficeChange(newValue)
                  }
                  renderTags={(value) =>
                    value.length > 0 ? (
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#162523',
                          pl: 0.5,
                        }}
                      >
                        {value.length} selected
                      </Typography>
                    ) : null
                  }
                  renderOption={(props, option, {selected}) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        size="small"
                        sx={{
                          padding: '0 12px 0 0',
                          color: '#E1E5E4',
                          '&.Mui-checked': {
                            color: '#34A290',
                          },
                        }}
                      />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        selectedHeadOffices.length === 0
                          ? 'Filter by head office'
                          : ''
                      }
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#162523',
                          '&::placeholder': {
                            color: '#162523',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    width: '200px',
                    '& .MuiOutlinedInput-root': {
                      minHeight: '40px',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      border: '1px solid #E1E5E4',
                      backgroundColor: '#FFFFFF',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#162523',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        border: '1px solid #E1E5E4',
                      },
                      '&.Mui-focused': {
                        border: '1px solid #E1E5E4',
                      },
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                      color: '#65706D',
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                      color: '#65706D',
                    },
                  }}
                  ListboxProps={{
                    sx: {
                      maxHeight: '200px',
                      overflow: 'auto',
                    },
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        borderRadius: '4px',
                        border: '1px solid #E1E5E4',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        '& .MuiAutocomplete-option': {
                          color: '#65706D',
                          fontSize: '14px',
                          padding: '8px 12px',
                          '&:hover': {
                            backgroundColor: '#F5F7F6',
                          },
                          '&[aria-selected="true"]': {
                            backgroundColor: 'transparent !important',
                            color: '#65706D !important',
                            '&:hover': {
                              backgroundColor: '#F5F7F6 !important',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#F5F7F6 !important',
                          },
                        },
                      },
                    },
                  }}
                />
              )}
              <Select
                value={selectedDropdown}
                onChange={(e) => handleDropdownChange(e)}
                sx={styles.dropdown}
                MenuProps={{
                  sx: styles.dropdownMenu,
                }}
              >
                {filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Button
                onClick={handleExportToExcel}
                sx={styles.exportButton}
                disabled={filteredOrgs.length === 0}
              >
                Export
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </div>
      <div style={styles.scrollableContent}>
        {isFetching ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{height: '50vh'}}
          >
            <Loading loading={true} message="Loading clients" />
          </Box>
        ) : filteredOrgs?.length === 0 ? (
          <Box sx={styles.emptyState}>
            <Typography sx={styles.emptyStateTitle}>
              No clients found
            </Typography>
            <Typography sx={styles.emptyStateSubtitle}>
              There are no clients available to display at this time.
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredOrgs?.map(
              ({
                org,
                name,
                fiscalYearEnd,
                userAssignedRole,
                mostRecentActivity,
                headOfficeId,
              }) => (
                <ListItem
                  key={org}
                  sx={styles.listItem}
                  onClick={() => handleOrgClick(org)}
                >
                  <Stack
                    direction="row"
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    sx={{width: '100%'}}
                  >
                    <div
                      style={{
                        ...styles.initialIcon,
                        backgroundColor: getColorByOrgHash(org),
                      }}
                    >
                      {name ? name?.charAt(0) : '?'}
                    </div>
                    <Stack sx={{flex: 3, pr: 3}} spacing={0.5}>
                      <Typography variant={'caption'} sx={styles.companyName}>
                        {name || 'Unset'}
                      </Typography>
                      <Typography variant={'caption'} sx={styles.subtleText}>
                        Last activity:{' '}
                        <TimeAgo timestamp={mostRecentActivity} />
                      </Typography>
                    </Stack>
                    <Stack sx={{flex: 1, alignItems: 'left'}} spacing={0.5}>
                      <Typography variant={'caption'} sx={styles.subtleText}>
                        Fiscal Year End
                      </Typography>
                      <Typography
                        variant={'caption'}
                        sx={styles.subtleSubtitle}
                      >
                        {formatFiscalYearEnd(fiscalYearEnd)}
                      </Typography>
                    </Stack>
                    {headOfficeOnParentOrganizationPageEnabled && (
                      <Stack sx={{flex: 1, alignItems: 'left'}} spacing={0.5}>
                        <Typography variant={'caption'} sx={styles.subtleText}>
                          Head Office
                        </Typography>
                        <Typography
                          variant={'caption'}
                          sx={styles.subtleSubtitle}
                        >
                          {headOfficeId || 'Unset'}
                        </Typography>
                      </Stack>
                    )}
                    <Stack sx={{flex: 1, alignItems: 'left'}} spacing={0.5}>
                      <Typography variant={'caption'} sx={styles.subtleText}>
                        Role
                      </Typography>
                      <Typography
                        variant={'caption'}
                        sx={{
                          ...styles.roleSubtitle,
                          backgroundColor:
                            ROLE_COLORS[userAssignedRole] ||
                            ROLE_COLORS.default,
                        }}
                      >
                        {userAssignedRole}
                      </Typography>
                    </Stack>
                    <Stack
                      sx={{
                        flex: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: 'auto',
                      }}
                    >
                      <img
                        src={ArrowRightIcon}
                        height={24}
                        width={24}
                        alt={'arrow-right'}
                        style={{marginLeft: '2rem', marginRight: '-0.5rem'}}
                      />
                    </Stack>
                  </Stack>
                </ListItem>
              )
            )}
          </List>
        )}
      </div>
    </div>
  );
};

ParentOrgPage.propTypes = {
  userFirstName: PropTypes.string,
};

export default ParentOrgPage;
